import React, { useEffect, useState } from 'react';
import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { I18n, T } from '../../../utils/I18n/I18n';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import moment from 'moment';
import Loader from '../../../components/Loader';
import { useUserContext } from '../../../context/UserContext';
import Button from '../../UI/Button/Button';
import FormInput from '../../UI/FormInput/FormInput';
import { usePopup } from '../../../context/PopupContext';
import './PersonalAreaClub.scss';
import readConfig from '../../../utils/readClientConfiguration';
import productionConfiguration from '../../../config/default.json';
import { CONNECT_CARD, CREATE_CARD, GET_CARD, ISSUE_TOKEN } from './queriesClub';
import { GET_CART } from '../../../queries/cart';
import { isSI } from '../../../utils/isHr';

const dateFormat = readConfig(productionConfiguration, 'dateFormat');
const timeFormat = readConfig(productionConfiguration, 'timeFormat');
const countryCode = readConfig(productionConfiguration, 'countryCode');

const PersonalAreaClub = () => {
    const [isVisible, setVisibleState] = useState(false);
    const { showAddToCartPopupMessage } = usePopup();
    const { user } = useUserContext();
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();
    const {
        register: register2,
        handleSubmit: handleSubmit2,
        formState: { errors: errors2 },
    } = useForm();

    const { data: card, loading, refetch } = useQuery(GET_CARD, {
        fetchPolicy: 'no-cache',
    });

    const [issueToken, { data: token }] = useLazyQuery(ISSUE_TOKEN);
    const [connectCard, { error: connectError, loading: connectLoading }] = useMutation(CONNECT_CARD, {
        fetchPolicy: 'no-cache',
        refetchQueries: [{ query: GET_CART }],
        awaitRefetchQueries: true,
    });

    const [createCard, { error: createError, loading: createLoading }] = useMutation(CREATE_CARD, {
        fetchPolicy: 'no-cache',
        refetchQueries: [{ query: GET_CART }],
        awaitRefetchQueries: true,
    });

    useEffect(() => {
        if (connectError) {
            showAddToCartPopupMessage(connectError.message, 'error', 4000);
        }
        if (createError) {
            showAddToCartPopupMessage(createError.message, 'error', 4000);
        }
    }, [connectError, createError, showAddToCartPopupMessage]);

    const formatLoyaltyDate = (date) => {
        return moment(date, 'YYYY-MM-DD').format('MM/D/YYYY');
    };

    const onSubmitNew = async (data) => {
        const { firstname, lastname, street, streetNumber, zip, city, country, telephone, isMobile, gender, date } = data;
        await createCard({
            variables: {
                firstname,
                lastname,
                street,
                streetNumber,
                zip,
                city,
                country,
                telephone,
                isMobile: !!isMobile,
                gender,
                dob: formatLoyaltyDate(date),
                email: user?.email || '', // Zabezpieczenie przed undefined
            },
        });
        if (!createError) {
            refetch();
        }
    };

    const onSubmitExisting = async ({ cardId, billId, agree, date }) => {
        await connectCard({ variables: { cardId, iagree: agree, billId: billId || date } });
        if (!connectError) {
            refetch();
        }
    };

    const closeModal = (e) => {
        if (e.target === e.currentTarget) {
            setVisibleState(false);
        }
    };

    if (token) {
        const { memberToken, validTo } = token.getLoyaltyToken?.token || {};
        const clubValidToDate = moment(validTo).add(1, 'hour');
        return (
            <>
        <span className="club__personal-area-title">
          <T id="club.header" />
        </span>
                <div className="club__card">
                    <p className="club__card-title">
                        <T id="club.clubCode" />
                    </p>
                    <div>
                        <p>
                            <T id="club.token" /> <span><b>{memberToken || 'N/A'}</b></span>
                        </p>
                        <p>
              <span>
                <T id="club.validTo" /> {clubValidToDate.isValid() ? clubValidToDate.format(`${dateFormat} ${timeFormat}`) : 'N/A'}
              </span>
                        </p>
                    </div>
                </div>
            </>
        );
    }

    if (card && card.historyCustomerLoyaltyCard?.loyalty?.[0]?.isActive) {
        const currentCard = card.historyCustomerLoyaltyCard.loyalty;
        return (
            <>
        <span className="club__personal-area-title">
          <T id="club.header" />
        </span>
                <div className="club__card">
                    <p className="club__card-title">
                        <T id="club.title" />
                    </p>
                    {currentCard && (
                        <p className="club__card-text">
                            <T id="club.yourCard" /> {currentCard[0].fullCardId}
                        </p>
                    )}
                    <p className="club__card-title">
                        <T id="club.cardStatus" />
                    </p>
                    {currentCard && (
                        <p className="club__card-text">
                            <T id="club.yourBonuses" /> {currentCard[0].saldoPoints || 0} <T id="club.bonuses" />
                        </p>
                    )}
                    <p className="club__card-text">
                        <T id="club.pointTable" />
                        <Link className="club__card-text__link" to="/emmezeta-klub#point-table">
                            <T id="product.here" />
                        </Link>
                    </p>
                    {!isSI && (
                        <div className="club__card-text mt-30">
                            <T id="club.info" />
                        </div>
                    )}
                </div>

                {!isSI && (
                    <Button
                        text={<T id="club.issueToken" />}
                        trait="profile-hover"
                        className="club__button"
                        onClick={() => issueToken()}
                    />
                )}

                <p className="club__card-title">
                    <T id="club.history" />
                </p>

                <div className="club__points-history">
                    <span className="club__points-col"><T id="club.date" /></span>
                    <span className="club__points-col"><T id="club.bonusesQuantity" /></span>
                    <span className="club__points-col"><T id="club.description" /></span>

                    {currentCard?.map(({ transDate, income, expense, store }, index) => (
                        <React.Fragment key={index}>
              <span>
                {transDate !== null
                    ? moment(transDate).format('L')
                    : moment().format('L')}
              </span>
                            <span>{income || expense || '0'}</span>
                            <span>
                {store} {income ? <T id="club.bought" /> : <T id="club.expenses" />}
              </span>
                        </React.Fragment>
                    ))}
                </div>
            </>
        );
    }

    return (
        <>
            {isVisible && (
                <div className="club__popup" onClick={closeModal}>
                    <div className="club__popup-modal">
                        <span className="club__popup-close" onClick={closeModal} />
                        <img className="club__popup-img" src="/images/racun_papir.jpg" alt="" />
                    </div>
                </div>
            )}

            {loading && <Loader />}

            {!loading && (
                <form onSubmit={handleSubmit2(onSubmitExisting)} className="club">
          <span className="club__personal-area-title">
            <T id="club.header" />
          </span>
                    <fieldset className="club__existing">
                        <h3 className="club__header">
                            <T id="club.clubMember" />
                        </h3>
                        <p className="club__info">
                            <T id="club.haveCard" />
                        </p>
                        <FormInput
                            register={register2}
                            errors={errors2}
                            type="text"
                            text={<T id="club.cardNumber" />}
                            name="cardId"
                        />

                        <div className="club__input">
                            <label><T id="club.birth" /></label>
                            <FormInput
                                register={register2}
                                errors={errors2}
                                type="date"
                                name="date"
                            />
                        </div>
                        <p className="club__text">
                            <T id="club.warning" />
                        </p>
                        <input
                            className="club__checkBox"
                            type="checkbox"
                            name="agree"
                            id="agree"
                            {...register2('agree', { required: true })}
                        />
                        <label className="club__label" htmlFor="agree">
                            <T id="club.agree" />
                        </label>
                        <br />
                        <Button type="submit" text="Povežite" trait="profile-hover" />

                        <div className="club__already-member">
                            <span><T id="club.saveInfo" /></span>
                            <Link className="club__already-member_link" to="/kontakt-i-usluge/korisnici/emmezeta-klub">
                                <T id="club.saveLink" />
                            </Link>
                        </div>
                    </fieldset>
                </form>
            )}

            {!loading && (
                <form onSubmit={handleSubmit(onSubmitNew)} className="club">
                    <fieldset className="club__new">
                        <h3 className="club__header">
                            <T id="club.newMember" />
                        </h3>
                        <p className="club__info">
                            <T id="club.noCard" />
                        </p>
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.name" />}
                            name="firstname"
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.lastname" />}
                            name="lastname"
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.street" />}
                            name="street"
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.streetNumber" />}
                            name="streetNumber"
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="address.additionalData" />}
                            name="additionalInfo"
                            isAdditionalInfo
                            required={false}
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.postcode" />}
                            name="zip"
                        />
                        <FormInput
                            register={register}
                            errors={errors}
                            type="text"
                            text={<T id="club.city" />}
                            name="city"
                        />

                        <I18n>
                            {t => (
                                <>
                                    <FormInput
                                        register={register}
                                        errors={errors}
                                        text={<T id="club.country" />}
                                        name="country"
                                    >
                                        <option>{t('club.croatia')}</option>
                                    </FormInput>

                                    <FormInput
                                        register={register}
                                        errors={errors}
                                        text={<T id="club.phoneType" />}
                                        name="isMobile"
                                    >
                                        <option value="">{t('club.mobileInfo')}</option>
                                        <option value={true}>{t('club.mobile')}</option>
                                        <option value={false}>{t('club.landline')}</option>
                                    </FormInput>

                                    <FormInput
                                        register={register}
                                        errors={errors}
                                        text={<T id="address.phone" />}
                                        type="tel"
                                        name="telephone"
                                    />
                                    <FormInput
                                        register={register}
                                        errors={errors}
                                        text={<T id="club.birth" />}
                                        type="date"
                                        name="date"
                                    />

                                    <FormInput
                                        register={register}
                                        errors={errors}
                                        text={<T id="club.gender" />}
                                        name="gender"
                                    >
                                        <option value=""></option>
                                        <option value="female">{t('club.female')}</option>
                                        <option value="male">{t('club.male')}</option>
                                    </FormInput>
                                    {countryCode === 'hr-HR' && (
                                        <FormInput
                                            isAdditionalInfo
                                            register={register}
                                            text={<T id="club.rent" />}
                                            errors={errors}
                                            name="rent"
                                        >
                                            <option value=""></option>
                                            <option>{t('club.yes')}</option>
                                            <option>{t('club.no')}</option>
                                        </FormInput>
                                    )}
                                </>
                            )}
                        </I18n>

                        {createLoading ? (
                            <Loader height="xxl" />
                        ) : (
                            <Button type="submit" text={<T id="club.connect" />} trait="profile-hover" />
                        )}
                    </fieldset>
                </form>
            )}
        </>
    );
};

export default PersonalAreaClub;